import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Bell, Loader2, ArrowRight } from "lucide-react";
import { KIND_ICON, KIND_TONE, fmtRelative } from "./notifications/notif_meta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 30000;
const BELL_LIMIT = 20; // ← latest 20 in the dropdown; older live on the All page.

/**
 * NotificationBell — header bell with unread badge + dropdown of the 20
 * latest notifications. A sticky footer at the bottom links to the full
 * All-Notifications page for older history + filters.
 *
 * Props:
 *  - portal: "customer" | "admin" — picks the right API surface
 *  - className: optional wrapper class for positioning
 */
export default function NotificationBell({ portal = "customer", className = "" }) {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [unread, setUnread] = useState(0);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const ref = useRef(null);

    const basePath = portal === "admin" ? "/admin/notifications" : "/notifications";
    const allPath  = portal === "admin" ? "/config/notifications" : "/notifications";

    const loadUnread = useCallback(async () => {
        try {
            const r = await axios.get(`${API}${basePath}/unread-count`, { withCredentials: true });
            setUnread(r.data?.count ?? 0);
        } catch { /* not signed in — silent */ }
    }, [basePath]);

    const loadList = useCallback(async () => {
        setLoading(true);
        try {
            const r = await axios.get(`${API}${basePath}?limit=${BELL_LIMIT}`, { withCredentials: true });
            setItems(r.data || []);
        } catch {
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [basePath]);

    // Poll unread count; refresh list when dropdown opens
    useEffect(() => {
        loadUnread();
        const t = setInterval(loadUnread, POLL_MS);
        return () => clearInterval(t);
    }, [loadUnread]);

    useEffect(() => { if (open) loadList(); }, [open, loadList]);

    // Click-outside / Escape to close
    useEffect(() => {
        if (!open) return;
        const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        document.addEventListener("mousedown", onClick);
        document.addEventListener("keydown", onKey);
        return () => { document.removeEventListener("mousedown", onClick); document.removeEventListener("keydown", onKey); };
    }, [open]);

    const markRead = async (id) => {
        try {
            await axios.post(`${API}${basePath}/mark-read`, { notification_id: id }, { withCredentials: true });
            setItems(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
            setUnread(u => Math.max(0, u - 1));
        } catch { /* noop */ }
    };

    const markAll = async () => {
        try {
            await axios.post(`${API}${basePath}/mark-all-read`, {}, { withCredentials: true });
            setItems(prev => prev.map(n => ({ ...n, read: true })));
            setUnread(0);
        } catch { /* noop */ }
    };

    const handleClick = (n) => {
        if (!n.read) markRead(n.id);
        setOpen(false);
        if (n.url) navigate(n.url);
    };

    return (
        <div ref={ref} className={`relative ${className}`}>
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                aria-label="Notifications"
                data-testid={`${portal}-bell`}
                className="relative p-2 rounded-lg hover:bg-stone-100 text-stone-700"
            >
                <Bell size={18} />
                {unread > 0 && (
                    <span
                        data-testid={`${portal}-bell-badge`}
                        className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-ink text-white text-[10px] font-bold flex items-center justify-center"
                    >
                        {unread > 99 ? "99+" : unread}
                    </span>
                )}
            </button>

            {open && (
                <div
                    role="dialog"
                    data-testid={`${portal}-bell-dropdown`}
                    className="fixed sm:absolute left-2 right-2 sm:left-auto sm:right-0 sm:w-96 top-12 bg-white rounded-xl shadow-2xl border border-stone-200 z-[60] flex flex-col overflow-hidden"
                    style={{ maxHeight: "min(75vh, 640px)" }}
                >
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100 bg-ivory-soft">
                        <p className="text-sm font-semibold text-stone-900">Notifications</p>
                        {unread > 0 && (
                            <button
                                onClick={markAll}
                                data-testid={`${portal}-bell-mark-all`}
                                className="text-[11px] text-rosegold hover:underline font-medium"
                            >
                                Mark all read
                            </button>
                        )}
                    </div>

                    {/* Scrollable list */}
                    <div className="flex-1 overflow-y-auto">
                        {loading && items.length === 0 ? (
                            <div className="px-4 py-8 text-center text-stone-400">
                                <Loader2 size={18} className="mx-auto animate-spin" />
                            </div>
                        ) : items.length === 0 ? (
                            <div className="px-4 py-10 text-center text-stone-400">
                                <Bell size={28} className="mx-auto mb-2 opacity-40" />
                                <p className="text-xs">No notifications yet</p>
                            </div>
                        ) : (
                            items.map(n => {
                                const Icon = KIND_ICON[n.kind] || Bell;
                                const tone = KIND_TONE[n.kind] || "text-stone-600 bg-stone-100";
                                return (
                                    <button
                                        key={n.id}
                                        type="button"
                                        onClick={() => handleClick(n)}
                                        data-testid={`${portal}-bell-item-${n.id}`}
                                        className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-ivory-soft border-b border-stone-50 ${!n.read ? "bg-rosegold/10" : ""}`}
                                    >
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${tone}`}>
                                            <Icon size={14} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between gap-2">
                                                <p className={`text-sm truncate ${!n.read ? "font-semibold text-stone-900" : "text-stone-700"}`}>
                                                    {n.title}
                                                </p>
                                                {!n.read && <span className="w-2 h-2 rounded-full bg-ink mt-1.5 flex-shrink-0" />}
                                            </div>
                                            {n.body && <p className="text-xs text-stone-500 mt-0.5 line-clamp-2">{n.body}</p>}
                                            <p className="text-[10px] text-stone-400 mt-1 uppercase tracking-wide">{fmtRelative(n.created_at)}</p>
                                        </div>
                                    </button>
                                );
                            })
                        )}
                    </div>

                    {/* Sticky "View all" footer */}
                    <button
                        type="button"
                        onClick={() => { setOpen(false); navigate(allPath); }}
                        data-testid={`${portal}-bell-view-all`}
                        className="flex items-center justify-center gap-1.5 px-4 py-3 border-t border-stone-200 bg-white text-[11.5px] font-bold uppercase tracking-[0.18em] text-rosegold hover:bg-ivory-soft hover:text-wine transition-colors"
                    >
                        View all notifications <ArrowRight size={12} />
                    </button>
                </div>
            )}
        </div>
    );
}
